import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { FtueDismissReason, FtueTone, IFtueTip } from 'src/app/core/ftue/ftue.models';
import { ftueTipI18nKeys } from 'src/app/core/ftue/ftue-tip.registry';
import { VoltaIconName } from '../../icons/volta-icons';
import { ButtonComponent } from '../../button/button.component';
import { IconComponent } from '../../icon/icon.component';

/** Tone → chip glyph. The card stays white; only the chip carries the tone. */
const TONE_ICONS: Record<FtueTone, VoltaIconName> = {
  alert: 'TriangleAlert',
  info: 'Lightbulb',
  new: 'Sparkles',
};

/**
 * The tip card. Deliberately dumb: it takes a registry entry, renders its
 * translated copy, and reports how the user closed it. It owns no eligibility
 * rule, no anchoring and no persistence — see `FtuePresenterService` and
 * `<ui-ftue-layer>`.
 *
 * Non-blocking by design (WB-7535 §3): no scrim, the page stays interactive,
 * and clicking outside counts as read. That makes the "you will only see this
 * once" line load-bearing rather than decorative, so it is not optional — and
 * the quiet link beside it is where a user goes to change that.
 */
@Component({
  selector: 'ui-coach-mark',
  standalone: true,
  imports: [NgIf, TranslateModule, ButtonComponent, IconComponent],
  templateUrl: './coach-mark.component.html',
  styleUrls: ['./coach-mark.component.scss'],
})
export class CoachMarkComponent {

  @Input() tip!: IFtueTip;

  /** Previewed from Settings: no "turn tips off", and nothing is recorded. */
  @Input() isPreview = false;

  @Output() dismissed = new EventEmitter<FtueDismissReason>();

  /** The user wants to see where these tips are managed. */
  @Output() settingsRequested = new EventEmitter<void>();

  get i18n(): ReturnType<typeof ftueTipI18nKeys> {
    return ftueTipI18nKeys(this.tip.id);
  }

  get toneIcon(): VoltaIconName {
    return TONE_ICONS[this.tip.tone];
  }
}
